import { Link } from 'wouter';

/**
 * Confirmed client results Coach Fe asked to publish on setthestandard.fit.
 * Same three photos confirmed for teamfgp.com on 2026-09-27.
 * Original file ids:
 * - 685f0b4e844c01d48f412e1c physique side by side
 * - 685f0b4e9aefd670f29a3cde physique, face covered
 * - 685f0b4f92d14528411b734d 6 lbs fat / 30 days board
 *
 * Added 2026-09-30 (client-proof build, approved photos #1-9 from the teamfgp.com archive).
 * Every face is covered with the same emoji style. No names or labels on any photo.
 * Source file ids: 685f0b4edafc5d5df3fa41e9, 685f0b4e2630c575c2ba55b1, 685f09b476699df0168ff49c,
 * 65df730251724d7e2ea7a3b9, 685f0b4e844c013321412e1b, 685f0b4e2630c51302ba55b2,
 * 66cce4d67fa631b6b013ff9f, 66c6412f6d087716f00372a6, 66c6412ff86495eb7c422336
 */
const FACE_COVERED_ALT = 'Front view before and after photos of a client, face covered with an emoji.';
const RESULTS_NOTE = 'Real clients. Real work. Results vary with effort and consistency.';

export const CONFIRMED_CLIENT_RESULTS = [
  {
    id: 'physique-side-by-side',
    src: '/images/results/physique-side-by-side.webp',
    width: 1400,
    height: 1400,
    kicker: 'Before and after',
    caption: 'Same stance. Same light.',
    alt: 'Side-by-side before and after photos of a client standing in the same pose and lighting.',
  },
  {
    id: 'physique-face-covered',
    src: '/images/results/physique-face-covered.webp',
    width: 1400,
    height: 1400,
    kicker: 'Before and after',
    caption: 'Face covered. The change is still there.',
    alt: 'Side-by-side before and after photos of a client with an emoji covering the face.',
  },
  {
    id: 'fat-loss-30-days',
    src: '/images/results/fat-loss-30-days.webp',
    width: 1600,
    height: 1600,
    kicker: '30 days',
    caption: '6 lbs of fat lost in 30 days. The board says The Standard. Set it.',
    alt: 'Whiteboard that reads 6 lbs fat lost in 30 days, The Standard, Set it.',
  },
  {
    id: 'result-01-front',
    src: '/images/results/result-01-front.webp',
    width: 960,
    height: 960,
    kicker: 'Before and after',
    caption: 'Softer through the middle on the left. Leaner and squared up on the right.',
    alt: FACE_COVERED_ALT,
  },
  {
    id: 'result-02-front',
    src: '/images/results/result-02-front.webp',
    width: 886,
    height: 886,
    kicker: 'Before and after',
    caption: 'Midsection tightened up. The chest and shoulders show the work.',
    alt: FACE_COVERED_ALT,
  },
  {
    id: 'result-03-front',
    src: '/images/results/result-03-front.webp',
    width: 1080,
    height: 1080,
    kicker: 'Before and after',
    caption: 'Waist came in. Shoulders and arms filled out.',
    alt: FACE_COVERED_ALT,
  },
  {
    id: 'result-04-front',
    src: '/images/results/result-04-front.webp',
    width: 1080,
    height: 901,
    kicker: 'Before and after',
    caption: 'Same door. Same stance. Tighter waist and a flatter midsection.',
    alt: FACE_COVERED_ALT,
  },
  {
    id: 'result-05-front',
    src: '/images/results/result-05-front.webp',
    width: 1200,
    height: 1200,
    kicker: 'Before and after',
    caption: 'Leaner through the middle. More shape in the arms on the right.',
    alt: FACE_COVERED_ALT,
  },
  {
    id: 'result-06-board',
    src: '/images/results/result-06-board.webp',
    width: 1200,
    height: 1200,
    kicker: "The scan doesn't lie.",
    caption: 'His board says it.',
    alt: 'Client holding a whiteboard. Face covered with an emoji.',
  },
  {
    id: 'result-07-front',
    src: '/images/results/result-07-front.webp',
    width: 990,
    height: 826,
    kicker: 'Before and after',
    caption: 'Smaller waist. Tighter midsection. Leaner legs.',
    alt: FACE_COVERED_ALT,
  },
  {
    id: 'result-08-front',
    src: '/images/results/result-08-front.webp',
    width: 775,
    height: 645,
    kicker: 'Before and after',
    caption: 'Leaner through the middle. More shape in the chest and shoulders.',
    alt: FACE_COVERED_ALT,
  },
  {
    id: 'result-09-front',
    src: '/images/results/result-09-front.webp',
    width: 775,
    height: 645,
    kicker: 'Before and after',
    caption: 'Same ink. Tighter waist. More definition.',
    alt: FACE_COVERED_ALT,
  },
] as const;

type ConfirmedResultsSectionProps = {
  id?: string;
  showPageLink?: boolean;
  imageLoading?: 'eager' | 'lazy';
  /** How many photos to show. Defaults to 6 when linking to /results, otherwise all of them. */
  limit?: number;
  /** Light: paper cards on the page canvas (the results page). Dark: the landing band. */
  tone?: 'dark' | 'light';
};

export function ConfirmedResultsSection({
  id = 'before-after',
  showPageLink = false,
  imageLoading = 'lazy',
  limit,
  tone = 'dark',
}: ConfirmedResultsSectionProps) {
  const items = CONFIRMED_CLIENT_RESULTS.slice(0, limit ?? (showPageLink ? 6 : CONFIRMED_CLIENT_RESULTS.length));
  return (
    <section
      data-surface={tone === 'dark' ? 'hero' : undefined}
      className={tone === 'dark' ? 'public-section public-section-dark' : 'public-section public-proof-light'}
      id={id}
      aria-labelledby={`${id}-title`}
    >
      <div className="public-section-heading">
        <p className="public-eyebrow">Before and After</p>
        <h2 className="public-h1" id={`${id}-title`}>Proof, Not Promises.</h2>
        <p className="public-lead">Confirmed client results. No names on the photos.</p>
      </div>
      <div className="public-proof-grid">
        {items.map((item, index) => (
          <figure className="public-proof-card" key={item.id}>
            <img
              src={item.src}
              alt={item.alt}
              width={item.width}
              height={item.height}
              loading={index < 3 ? imageLoading : 'lazy'}
              decoding="async"
            />
            <figcaption>
              <span className="public-proof-kicker">{item.kicker}</span>
              <p>{item.caption}</p>
            </figcaption>
          </figure>
        ))}
      </div>
      <p className="public-proof-note">{RESULTS_NOTE}</p>
      {showPageLink && (
        <p className="public-proof-more">
          <a className="public-text-link" href="/results">See the results page</a>
        </p>
      )}
    </section>
  );
}

export function ConfirmedResultsStrip() {
  return (
    <section className="mt-8 rounded-xl border p-6" aria-labelledby="apply-before-after-title">
      <h2 id="apply-before-after-title" className="text-xl font-bold">Before and after</h2>
      <p className="mt-3">Confirmed client results. No names on the photos.</p>
      <div className="mt-5 grid gap-4 md:grid-cols-3">
        {CONFIRMED_CLIENT_RESULTS.slice(0, 6).map((item) => (
          <figure className="m-0 overflow-hidden rounded-xl border" key={item.id}>
            <img
              src={item.src}
              alt={item.alt}
              width={item.width}
              height={item.height}
              loading="lazy"
              decoding="async"
              className="aspect-square w-full object-cover"
            />
            <figcaption className="p-3 text-sm leading-6">{item.caption}</figcaption>
          </figure>
        ))}
      </div>
      <p className="mt-4 text-sm leading-6">{RESULTS_NOTE}</p>
      <p className="mt-4">
        <Link className="underline" href="/results">See the results page</Link>
      </p>
    </section>
  );
}
